import { useParams } from 'react-router-dom';
import { Padding } from 'Components/Padding';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import CertificateDownload from 'Components/CertificateDownload/CertificateDownload';
import CourseStatusChip from 'Components/CourseStatusChip';
import GenericTableText from 'Components/GenericTableText/GenericTableText';
import SideBarContainer from 'Containers/SideBarContainer';
import { useHistoryCourseDetailPage } from 'Hooks/useHistoryCourseDetailPage';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import ReportsDataGrid from 'Pages/reports/components/ReportsDataGrid/ReportsDataGrid';
import { ReportsButtonGroup } from 'Pages/reports/components/ReportsButtonGroup/ReportsButtonGroup';
import { formatDate } from 'Utils/format-date';
import { toReportsPagination } from 'Pages/reports/utils/pagination';
import { HistoryCourseEmployeesFilter } from './components/HistoryCourseEmployeesFilter/HistoryCourseEmployeesFilter';
import TotalCourseEmployeesDetails from './components/TotalCourseEmployeesDetails/TotalCourseEmployeesDetails';
import { CoursesReportIcon, EmployeesReportIcon } from 'Pages/reports/components/icons';
import type { GridColDef } from '@mui/x-data-grid';

interface Params {
  courseId: string;
}

interface Row {
  id: string;
  userId: string;
  payrollNum: number | string;
  name: string;
  terminal: string;
  department: string;
  jobTitle: string;
  gender: string;
  age: number | string;
  createdAt: string;
  finishedAtRaw: string | null;
  statusId: string;
  statusName: string;
  instructorName: string | null;
  certificationId: string | null;
}

export default function Page() {
  const { courseId } = useParams<Params>();

  const {
    t2,
    data,
    pagination,
    totals,
    loading,
    error,
    course,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    handleDownloadCertificate,
    handleRowClick,
  } = useHistoryCourseDetailPage(courseId);

  const buttons = [
    {
      icon: <EmployeesReportIcon />,
      tooltip: t2('employees_report_tooltip'),
      route: '/reporte/historial/empleados',
    },
    { icon: <CoursesReportIcon />, tooltip: t2('courses_report_tooltip'), route: '/reporte/historial/cursos' },
  ];

  const columns: GridColDef[] = [
    {
      field: 'payrollNum',
      headerName: t2('payroll_number'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'name', headerName: t2('employee_name'), flex: 1.5 },
    { field: 'terminal', headerName: t2('terminal'), flex: 1 },
    { field: 'department', headerName: t2('department'), flex: 1 },
    { field: 'jobTitle', headerName: t2('job_title'), flex: 1 },
    { field: 'gender', headerName: t2('gender'), flex: 0.8 },
    {
      field: 'age',
      headerName: t2('age'),
      flex: 0.6,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'createdAt', headerName: t2('start_date'), flex: 1 },
    {
      field: 'finishedAt',
      headerName: t2('finished_date'),
      flex: 1,
      renderCell: params =>
        params.row.finishedAtRaw ? (
          formatDate(params.row.finishedAtRaw)
        ) : (
          <GenericTableText text={t2('not_finished')} />
        ),
    },
    {
      field: 'statusName',
      headerName: t2('status'),
      flex: 1.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => <CourseStatusChip statusId={params.row.statusId} status={params.row.statusName} />,
    },
    {
      field: 'instructor',
      headerName: t2('instructor'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: params =>
        params.row.instructorName ? params.row.instructorName : <GenericTableText text={t2('no_instructor')} />,
    },
    {
      field: 'downloadCertificate',
      headerName: t2('certificate'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: params => (
        <CertificateDownload
          certificationId={params.row.certificationId}
          onDownload={() => handleDownloadCertificate(params.row.id, params.row.certificationId ?? '')}
        />
      ),
    },
  ];

  const rows: Row[] = data.map(employee => ({
    id: employee.id,
    userId: employee.userId,
    payrollNum: employee.payrollNum,
    name: employee.name,
    terminal: employee.terminal?.name || '-',
    department: employee.department?.name || '-',
    jobTitle: employee.jobTitle?.name || '-',
    gender: employee.gender || '-',
    age: employee.age ?? '-',
    createdAt: employee.createdAt ? formatDate(employee.createdAt) : '-',
    finishedAtRaw: employee.finishedAt ?? null,
    statusId: employee.status?.id || '',
    statusName: employee.status?.name || '-',
    instructorName: employee.instructorName ?? null,
    certificationId: employee.certificationId,
  }));

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: t2('history_reports'), path: '/reporte/historial/cursos' },
            { label: t2('course_detail'), path: `/reporte/historial/cursos/${courseId}` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={`"${course?.name || ''}"`} />}
        buttons={<ReportsButtonGroup buttons={buttons} />}
        onExport={handleExport}
      />
      <Padding padding="1rem">
        <TotalCourseEmployeesDetails totals={totals} />
        <HistoryCourseEmployeesFilter onSearch={handleSearch} />
        <Padding padding="0.5rem 0" />
        <ReportsDataGrid<Row>
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          pagination={toReportsPagination(pagination)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={row => handleRowClick(row)}
          noDataMessage={t2('no_data')}
        />
      </Padding>
    </SideBarContainer>
  );
}
